/**
 * Resolve an /api/images/<key> reference to a directly-fetchable URL.
 *
 * XHR callers must NOT follow the endpoint's 302. A cross-origin redirect taints
 * the request's origin to "null", S3 answers without an Access-Control-Allow-Origin
 * for that, and the browser discards the response — which is what silently broke
 * lease e-signing and the invoice QR when /api/images switched to redirects.
 *
 * Asking for JSON returns the presigned URL instead, which the caller then
 * fetches with its real Origin (allowed by the bucket CORS rule). `<img>` tags
 * need none of this and keep following the redirect.
 *
 * Accept is a CORS-safelisted request header, so this adds no preflight.
 */
export class ImageNotFoundError extends Error {
  constructor(key: string) {
    super(`Image not found: ${key}`)
    this.name = 'ImageNotFoundError'
  }
}

export async function resolveImageUrl(key: string): Promise<string> {
  const base = import.meta.env.VITE_API_URL || ''
  const res = await fetch(`${base}/api/images/${key}`, {
    headers: { Accept: 'application/json' },
  })
  // 404 means the object is genuinely gone. Every other failure — offline, DNS,
  // a 5xx — is NOT evidence of absence, and callers that rebuild missing files
  // must not act on it.
  if (res.status === 404) throw new ImageNotFoundError(key)
  if (!res.ok) throw new Error(`Could not resolve image ${key} (${res.status})`)
  const { url } = (await res.json()) as { url: string }
  return url
}

/** Resolve, then fetch the bytes. Throws ImageNotFoundError only on a real 404. */
export async function fetchImageBytes(key: string): Promise<ArrayBuffer> {
  const url = await resolveImageUrl(key)
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Could not download image ${key} (${res.status})`)
  return await res.arrayBuffer()
}
